import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { formatFacetValue } from 'wsm-srp-utilities';
import { trackEvent } from 'ddc-track-event';
import { useRequestData } from 'wsm-common-data';

const RangeForm = ({
	id,
	minValue,
	maxValue,
	minInput,
	maxInput,
	minLabel,
	maxLabel,
	handleSubmit,
	isCurrency,
	facetTitle
}) => {
	const [currentMinVal, setCurrentMin] = useState(minInput);
	const [currentMaxVal, setCurrentMax] = useState(maxInput);
	const { widgetName, windowId, locale } = useRequestData();

	const isNumber = (num) => !Number.isNaN(parseInt(num, 10));
	const getNumberFromInput = (str) =>
		parseInt(str.replace(/[^0-9]/g, ''), 10);

	useEffect(() => {
		setCurrentMin(minInput);
		setCurrentMax(maxInput);
	}, [minInput, maxInput]);

	const handleFormSubmit = () => {
		if (isNumber(currentMinVal) && isNumber(currentMaxVal)) {
			handleSubmit(currentMinVal, currentMaxVal);
		}

		trackEvent(widgetName, windowId, {
			action: 'applied',
			element: `${id} selection`,
			elementCTA: 'Text input',
			result: 'Results updated with facet added',
			dataToExcludeFromWidgetUserEvent: {
				fieldValue: `${currentMinVal}-${currentMaxVal}`,
				facetId: id,
				facetTitle
			}
		});
	};

	const handleRangeUpdate = (e, type) => {
		const number = getNumberFromInput(e.target.value);
		const isWithinRange =
			number >= minValue &&
			number <= maxValue &&
			((type === 'min' && number <= maxInput) ||
				(type === 'max' && number >= minInput));
		if (isWithinRange) {
			if (type === 'min') {
				setCurrentMin(number);
			} else if (type === 'max') {
				setCurrentMax(number);
			}
		} else {
			setCurrentMin(minInput);
			setCurrentMax(maxInput);
		}
		handleFormSubmit();
	};

	const handleKeyDown = (e, type) => {
		if (e.keyCode === 13 && !e.shiftKey) {
			handleRangeUpdate(e, type);
		}
	};

	const handleChange = (e, type) => {
		const number = getNumberFromInput(e.target.value);

		if (number) {
			if (type === 'min') {
				setCurrentMin(number);
			} else if (type === 'max') {
				setCurrentMax(number);
			}
		} else if (type === 'min') {
			setCurrentMin(e.target.value);
		} else if (type === 'max') {
			setCurrentMax(e.target.value);
		}
	};

	const minInputValidation = isNumber(currentMinVal) ? null : 'error';
	const maxInputValidation = isNumber(currentMaxVal) ? null : 'error';

	return (
		<Form className="range-form">
			<FormGroup
				validationState={minInputValidation}
				id={`${id}-group-min`}
				controlId={`${id}-min`}
			>
				<ControlLabel htmlFor={`${id}-min`}>{minLabel}</ControlLabel>
				<FormControl
					id={`${id}-min`}
					className="font-size-ios-zoom-override"
					value={formatFacetValue(
						id,
						isCurrency,
						currentMinVal,
						locale
					)}
					onChange={(e) => handleChange(e, 'min')}
					onBlur={(e) => handleRangeUpdate(e, 'min')}
					onKeyDown={(e) => handleKeyDown(e, 'min')}
				/>
			</FormGroup>
			<FormGroup
				validationState={maxInputValidation}
				id={`${id}-group-max`}
				controlId={`${id}-max`}
			>
				<ControlLabel htmlFor={`${id}-max`}>{maxLabel}</ControlLabel>
				<FormControl
					id={`${id}-max`}
					className="font-size-ios-zoom-override"
					value={formatFacetValue(
						id,
						isCurrency,
						currentMaxVal,
						locale
					)}
					onChange={(e) => handleChange(e, 'max')}
					onBlur={(e) => handleRangeUpdate(e, 'max')}
					onKeyDown={(e) => handleKeyDown(e, 'max')}
				/>
			</FormGroup>
		</Form>
	);
};

RangeForm.propTypes = {
	id: PropTypes.string.isRequired,
	minValue: PropTypes.number.isRequired,
	maxValue: PropTypes.number.isRequired,
	minInput: PropTypes.number.isRequired,
	maxInput: PropTypes.number.isRequired,
	minLabel: PropTypes.string.isRequired,
	maxLabel: PropTypes.string.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	isCurrency: PropTypes.bool.isRequired,
	facetTitle: PropTypes.string.isRequired
};

export default RangeForm;
